import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useTenants } from '../../hooks/useTenants';
import { Button } from '../ui/button';

function SessionRecovery() {
  const { error, retryBootstrap, logout } = useAuth();
  return (
    <main className='mx-auto max-w-md space-y-4 p-6'>
      <h1 className='text-2xl font-semibold tracking-tight'>Verify session</h1>
      <p role='alert' className='text-sm text-destructive'>
        {error}
      </p>
      <Button onClick={retryBootstrap}>Retry verification</Button>
      <Button variant='outline' onClick={() => void logout()}>
        Sign out
      </Button>
    </main>
  );
}

function TenantRecovery() {
  const { error, switchTenant, logout } = useAuth();
  const { data: tenants = [], isLoading, isError, refetch } = useTenants();
  const selector = localStorage.getItem('tenant_id');
  const select = id => {
    void switchTenant(id).catch(() => {});
  };
  return (
    <main className='mx-auto max-w-md space-y-4 p-6'>
      <h1 className='text-2xl font-semibold tracking-tight'>
        Select organization
      </h1>
      <p role='alert' className='text-sm text-destructive'>
        {error || 'Select an organization to verify access.'}
      </p>
      {selector && (
        <Button onClick={() => select(selector)}>Retry verification</Button>
      )}
      {isLoading && <p role='status'>Loading organizations...</p>}
      {isError && (
        <div role='alert'>
          <p>Organizations could not be loaded.</p>
          <Button variant='outline' onClick={() => refetch()}>
            Retry organizations
          </Button>
        </div>
      )}
      {!isLoading && !isError && tenants.length === 0 && (
        <p>No organizations available. Contact your administrator.</p>
      )}
      <div className='flex flex-col gap-2'>
        {tenants.map(tenant => (
          <Button
            key={tenant.id}
            variant='outline'
            onClick={() => select(tenant.id)}
          >
            {tenant.name}
          </Button>
        ))}
      </div>
      <Button variant='outline' onClick={() => void logout()}>
        Sign out
      </Button>
    </main>
  );
}

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user, token, bootstrapFailed } =
    useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
        <div className='sm:mx-auto sm:w-full sm:max-w-md'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
            <p className='mt-4 text-sm text-gray-600'>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (user && token) return <TenantRecovery />;
    if (bootstrapFailed) return <SessionRecovery />;
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
